import React from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import ImageView from '../components/ImageView';
import LiveBadge from './LiveBadge';
import PrimaryButton from '../components/PrimaryButton';
import { formatLiveTime } from '../utils/dateUtils';
import AuctionHouseComponent from '../components/common/AuctionHouse';
// import 'swiper/css';
// import 'swiper/css/pagination';

// 경매 카드 컨테이너
const AuctionCardContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  min-height: 370px;
  
  @media (min-width: 768px) {
    min-height: 430px;
  }
  
  @media (min-width: 1024px) {
    min-height: 480px;
  }
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// 벳지 컨테이너
const BadgeContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: auto;
  
`;

// 이미지 스와이퍼 컨테이너
const ImageSwiperContainer = styled.div`
  width: 100%;
  height: 240px;
  margin-top: 40px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  
  @media (min-width: 768px) {
    height: 280px;
  }
  
  @media (min-width: 1024px) {
    height: 320px;
  }
  
  .swiper {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
    overflow: hidden !important;
  }
  
  .swiper-wrapper {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
  }
  
  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    min-width: 100% !important;
    max-width: 100% !important;
    
    /* ImageView가 전체 크기를 차지하도록 */
    > div {
      width: 100% !important;
      height: 100% !important;
    }
    
    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
  }
  
  .swiper-pagination {
    bottom: 8px !important;
    z-index: 10;
    
    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: #ccc !important;
      opacity: 1 !important;
      margin: 0 3px !important;
      border: 1px solid white !important;
    }
    
    .swiper-pagination-bullet-active {
      background: #007bff !important;
      border: 1px solid white !important;
    }
  }
`;

// 옥션 정보 컨테이너
const AuctionInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 경매사명
const AuctionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
  line-height: 1.3;
  text-align: left;
  align-self: flex-start;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 경매 종료 오버레이 (LotCard와 동일한 스타일)
const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

// 날짜 정보
const DateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  width: 100%;
`;

const DateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const DateValue = styled.span`
  font-size: 12px;
  color: #666;
`;

// 가격 표시용 컨테이너
const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const PriceUnit = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const PriceAmount = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #69b5b0;
`;

// 결제 상태 칩
const PaymentStatusChip = styled.div<{ $isPaid: boolean }>`
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$isPaid ? '#fff' : '#fff'};
  color: ${props => props.$isPaid ? '#297873' : '#b6a488'};
  border: 1px solid ${props => props.$isPaid ? '#297873' : '#b6a488'};
`;

// 결제 버튼 컨테이너
const PaymentButtonContainer = styled.div`
  margin-top: 12px;
  padding-top: 12px;
`;

interface AuctionCardProps {
  auction: any;
  lang: 'ko' | 'en';
  type?: 'ongoing' | 'won' | 'bid';
  onClick?: (auction: any) => void;
  onPayment?: (auction: any) => void;
  extraData?: {
    wonLotCount?: number;
    totalPayment?: number;
    completedAt?: string;
    isPaid?: boolean;
    paymentRequestDate?: string;
    paymentDueDate?: string;
  };
  // 즐겨찾기 관련 props 추가
  onFavoriteToggle?: (e: React.MouseEvent, auctionIndex: number) => Promise<void>;
  showFavoriteIcon?: boolean;
  isFavorite?: boolean;
}

const AuctionCard: React.FC<AuctionCardProps> = ({ 
  auction, 
  lang, 
  type = 'ongoing', 
  onClick,
  onPayment,
  extraData,
  onFavoriteToggle,
  showFavoriteIcon = true,
  isFavorite = false
}) => {
  
  // 경매가 종료되었는지 확인하는 함수
  const isAuctionEnded = () => {
    if (!auction.auctionEndAt) return false;
    return new Date() > new Date(auction.auctionEndAt);
  };
  
  // 다국어 텍스트
  const texts = {
    ko: {
      ongoing: {
        photoSubmissionDate: '사전입찰 일시',
        auctionDate: '경매 기간'
      },
      won: {
        wonLotCount: '낙찰된 LOT 개수',
        totalPayment: '총 결제 금액',
        bidCompleted: '입찰 완료',
        paymentCompleted: '결제완료',
        paymentPending: '결제대기',
        paymentCompletedDate: '결제완료일',
        paymentRequestDate: '결제요청일',
        paymentDueDate: '결제요청마감일',
        payButton: '결제하기'
      },
      bid: {
        photoSubmissionDate: '사전입찰 일시',
        auctionDate: '경매 기간'
      },
      auctionEnded: '경매 종료'
    },
    en: {
      ongoing: {
        photoSubmissionDate: 'Photo Bid Date',
        auctionDate: 'Auction Period'
      },
      won: {
        wonLotCount: 'Won LOT Count',
        totalPayment: 'Total Payment',
        bidCompleted: 'Bid Completed',
        paymentCompleted: 'Completed',
        paymentPending: 'Pending',
        paymentCompletedDate: 'Payment Completed',
        paymentRequestDate: 'Payment Request Date',
        paymentDueDate: 'Payment Due Date',
        payButton: 'Pay Now'
      },
      bid: {
        photoSubmissionDate: 'Photo Bid Date',
        auctionDate: 'Auction Period'
      },
      auctionEnded: 'Auction Ended'
    }
  };

  // 날짜 포맷팅 함수 (국제화 적용)
  const formatDate = (isoString: string): string => {
    try {
      const targetDate = new Date(isoString);
      const month = targetDate.getMonth() + 1;
      const date = targetDate.getDate();
      const hours = targetDate.getHours().toString().padStart(2, '0');
      const minutes = targetDate.getMinutes().toString().padStart(2, '0');
      
      // 요일 이름
      const dayNamesKo = ['일', '월', '화', '수', '목', '금', '토'];
      const dayNamesEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = lang === 'ko' ? dayNamesKo[targetDate.getDay()] : dayNamesEn[targetDate.getDay()];

      if (lang === 'ko') {
        // 한국어: 07/17(금) 13:00
        const monthStr = month.toString().padStart(2, '0');
        const dateStr = date.toString().padStart(2, '0');
        return `${monthStr}/${dateStr}(${dayName}) ${hours}:${minutes}`;
      } else {
        // 영어: Jul 17(Fri) 13:00 (KR TIME)
        const monthName = monthNamesEn[targetDate.getMonth()];
        return `${monthName} ${date}(${dayName}) ${hours}:${minutes} (KR TIME)`;
      }
    } catch (error) {
      return isoString;
    }
  };

  // 경매 기간 포맷팅 함수
  const formatAuctionPeriodLocal = (startDate: string, endDate: string): string => {
    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(endDate);
    
    const startParts = startFormatted.split(' ');
    const endParts = endFormatted.split(' ');
    
    const startDatePart = startParts[0]; // 07/17(금) or Jul 17(Fri)
    const startTimePart = startParts[1]; // 13:00
    const endDatePart = endParts[0]; // 07/17(금) or Jul 17(Fri)  
    const endTimePart = lang === 'ko' ? endParts[1] : endParts[1]; // 13:00
    
    if (startDatePart === endDatePart) {
      // 같은 날인 경우
      if (lang === 'ko') {
        return `${startDatePart} ${startTimePart} ~ ${endTimePart}`;
      } else {
        return `${startDatePart} ${startTimePart} ~ ${endTimePart}`;
      }
    } else {
      // 다른 날인 경우
      if (lang === 'ko') {
        // 한국어: 07/17(금) 13:00 ~ 18(토) 15:00
        const endDay = endDatePart.split('/')[1]; // 18(토) 부분 추출
        return `${startDatePart} ${startTimePart} ~ ${endDay} ${endTimePart}`;
      } else {
        // 영어: Jul 17(Fri) 13:00 ~ 18(Sat) 15:00 (KR TIME)
        const endDayWithName = endDatePart.split(' ')[1]; // 18(Sat) 부분 추출
        return `${startDatePart} ${startTimePart} ~ ${endDayWithName} ${endTimePart}`;
      }
    }
  };

  // 이미지 URL 처리 함수
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // 경매의 모든 이미지 URL을 가져오는 함수 (실제 이미지만, 최대 3장 제한)
  const getAllImageUrls = (auction: any): string[] => {
    const imageUrls: string[] = [];
    
    // lots를 순회하면서 모든 이미지를 수집 (최대 3장까지만)
    if (auction.lots && Array.isArray(auction.lots)) {
      auction.lots.forEach((lot: any) => {
        if (imageUrls.length >= 3) return; // 이미 3장에 도달했으면 중단
        
        if (lot.images && Array.isArray(lot.images)) {
          lot.images.forEach((imagePath: string) => {
            if (imageUrls.length >= 3) return; // 이미 3장에 도달했으면 중단
            
            if (imagePath && imagePath.trim() !== "") {
              imageUrls.push(getProfileImageSrc(imagePath));
            }
          });
        }
      });
    }
    
    return imageUrls;
  };

  // 가격을 unit과 amount로 분리하는 함수
  const formatPriceComponents = (price: number): { unit: string; amount: string } => {
    if (lang === 'ko') {
      return {
        unit: 'KRW',
        amount: price.toLocaleString()
      };
    } else {
      const usdPrice = Math.round(price / 1300);
      return {
        unit: 'USD',
        amount: usdPrice.toLocaleString()
      };
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(auction);
    }
  };

  const handlePaymentClick = () => {
    devLog('AuctionCard handlePaymentClick 호출됨, auction:', auction);
    
    if (!auction) {
      devError('AuctionCard: auction 데이터가 없습니다.');
      return;
    }
    
    if (onPayment) {
      onPayment(auction);
    }
  };

  // 배지 컨텐츠 결정 (type이 won일 때는 결제 상태 칩 표시)
  const getBadgeContent = () => {
    if (type === 'won') {
      const isPaid = extraData?.isPaid ?? false;
      const statusText = isPaid 
        ? (texts[lang].won as any).paymentCompleted 
        : (texts[lang].won as any).paymentPending;
      return (
        <PaymentStatusChip $isPaid={isPaid}>
          {statusText}
        </PaymentStatusChip>
      );
    }
    return formatLiveTime(auction.auctionStartAt, lang, auction.auctionEndAt);
  };

  // live 뱃지를 표시할지 결정하는 함수
  const shouldShowLiveBadge = () => {
    if (type === 'won') return true; // 낙찰 타입은 항상 표시 (결제 상태 칩이므로)
    return !isAuctionEnded(); // 경매가 종료되지 않았을 때만 표시
  };

  // 우측 상단 텍스트 결정
  const getRightTopText = () => {
    if (type === 'won') {
      const isPaid = extraData?.isPaid ?? false;
      if (isPaid) {
        // 결제완료 상태: 날짜 + '결제완료' 텍스트
        const completedDate = extraData?.completedAt ? formatDate(extraData.completedAt) : '';
        const completedText = (texts[lang].won as any).paymentCompleted;
        return `${completedDate} ${completedText}`;
      } else {
        // 결제대기 상태: 날짜 + 결제요청마감일만
        const dueDate = extraData?.paymentDueDate ? formatDate(extraData.paymentDueDate) : '';
        const dueText = (texts[lang].won as any).paymentDueDate;
        return `${dueDate} ${dueText}`;
      }
    }
    return null;
  };

  return (
    <AuctionCardContainer onClick={handleCardClick}>
      {/* <BadgeContainer>
        {shouldShowLiveBadge() && (
          <>
            {type === 'won' ? (
              getBadgeContent()
            ) : (
              <LiveBadge liveTime={getBadgeContent() as string} isMobile={true} />
            )}
          </>
        )}
        {getRightTopText() && (
          <div style={{
            color: '#262626',
            fontSize: '12px',
            fontWeight: 500
          }}>
            {getRightTopText()}
          </div>
        )}
      </BadgeContainer> */}
      
      <ImageSwiperContainer onClick={(e) => e.stopPropagation()}>
        {(() => {
          const imageUrls = getAllImageUrls(auction);
          if (imageUrls.length < 1) {
            // 이미지가 1개 이하면 기본 ImageView 사용
            return (
              <ImageView
                imageUrl="/common/nodata.svg"
                width={240}
                height={240}
                borderRadius={8}
                alt={auction.name}
                objectFit="cover"
              />
            );
          } else {
            // 이미지가 2개 이상이면 스와이퍼 사용
            return (
              <Swiper
                modules={[Pagination]}
                slidesPerView={1}
                slidesPerGroup={1}
                spaceBetween={0}
                pagination={{ clickable: true }}
                loop={imageUrls.length > 1}
                centeredSlides={true}
                allowTouchMove={true}
                touchStartPreventDefault={true}
                grabCursor={true}
                style={{ 
                  width: '320px', 
                  height: '290px',
                  maxWidth: '320px',
                  maxHeight: '290px',
                  overflow: 'visible',
                  paddingBottom: '30px'
                }}
              >
                {imageUrls.map((imageUrl, index) => (
                  <SwiperSlide key={index} style={{ 
                    width: '280px', 
                    height: '290px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <ImageView
                      imageUrl={imageUrl}
                      width={280}
                      height={260}
                      borderRadius={8}
                      alt={`${auction.name} ${index + 1}`}
                      objectFit="contain"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            );
          }
        })()}
        
        {/* 경매 종료 오버레이 - ongoing 또는 bid 타입이고 경매가 종료되었을 때만 표시 */}
        {(type === 'ongoing' || type === 'bid') && isAuctionEnded() && (
          <EndedAuctionOverlay>
            <EndedAuctionText>{texts[lang].auctionEnded}</EndedAuctionText>
          </EndedAuctionOverlay>
        )}
        
        {/* 즐겨찾기 아이콘 */}
        {/* {showFavoriteIcon && (
          <FavoriteIcon
            isFavorite={isFavorite}
            onClick={onFavoriteToggle ? (e) => onFavoriteToggle(e, auction.index) : undefined}
          />
        )} */}
      </ImageSwiperContainer>
      
      <AuctionInfoContainer>
        <AuctionHouseComponent 
          auctioneerName={auction.auctioneer?.name || '경매사'}
          auctioneerIndex={auction.auctioneer?.index || 0}
        />
        
        <AuctionTitle>{auction.name}</AuctionTitle>
        
        {type === 'ongoing' && (
          <>
            <DateInfo>
              <DateLabel>{(texts[lang].ongoing as any).photoSubmissionDate}</DateLabel>
              <DateValue>{formatAuctionPeriodLocal(auction.preBidStartAt, auction.preBidEndAt)}</DateValue>
            </DateInfo>
            
            <DateInfo>
              <DateLabel>{(texts[lang].ongoing as any).auctionDate}</DateLabel>
              <DateValue>{formatAuctionPeriodLocal(auction.auctionStartAt, auction.auctionEndAt)}</DateValue>
            </DateInfo>
          </>
        )}

        {type === 'won' && extraData && (
          <>
            <DateInfo>
              <DateLabel>{(texts[lang].won as any).wonLotCount}</DateLabel>
              <DateValue>{extraData.wonLotCount?.toLocaleString() || '0'}</DateValue>
            </DateInfo>
            
            <DateInfo>
              <DateLabel>{(texts[lang].won as any).totalPayment}</DateLabel>
              {extraData.totalPayment ? (
                <PriceContainer>
                  <PriceUnit>{formatPriceComponents(extraData.totalPayment).unit}</PriceUnit>
                  <PriceAmount>{formatPriceComponents(extraData.totalPayment).amount}</PriceAmount>
                </PriceContainer>
              ) : (
                <DateValue>-</DateValue>
              )}
            </DateInfo>
          </>
        )}

        {type === 'bid' && (
          <>
            <DateInfo>
              <DateLabel>{(texts[lang].bid as any).photoSubmissionDate}</DateLabel>
              <DateValue>{formatAuctionPeriodLocal(auction.preBidStartAt, auction.preBidEndAt)}</DateValue>
            </DateInfo>
            
            <DateInfo>
              <DateLabel>{(texts[lang].bid as any).auctionDate}</DateLabel>
              <DateValue>{formatAuctionPeriodLocal(auction.auctionStartAt, auction.auctionEndAt)}</DateValue>
            </DateInfo>
          </>
        )}
      </AuctionInfoContainer>
      
      {/* 결제 버튼 - 낙찰 타입이고 결제 대기 상태일 때만 표시 */}
      {/* {type === 'won' && extraData && !extraData.isPaid && onPayment && (
        // <PaymentButtonContainer>
        //   <PrimaryButton onClick={handlePaymentClick}>
        //     {(texts[lang].won as any).payButton}
        //   </PrimaryButton>
        // </PaymentButtonContainer>
      )} */}
    </AuctionCardContainer>
  );
};

export default AuctionCard;
